import React from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { complete_type_lookup } from '../../common/Types';
import { connectivity_colour_lookup } from '../../common/Colours';
import { Icon } from '@iconify/react';
import externalLink from '@iconify/icons-tabler/external-link';
import bxsRightArrow from '@iconify/icons-bx/bxs-right-arrow';
import bxsDownArrow from '@iconify/icons-bx/bxs-down-arrow';
import { getHost } from '../../../api/host'
import { DateTime } from 'luxon';
import { PositionDetails } from './PositionDetails';
import { notUndefined, isUndefinedOrNull } from '../../../lib/typeCheck';
import { ConnectivityIcon, ExceedancesIcon, BatteryIcon } from '../../common/Icons';

const Row = styled.tr`
  colour: #292D34;
  font-size: 18px;
  padding 20px 0;
`;

const Cell = styled.td.attrs(props => ({
  colSpan: props.colSpan
}))<{ colSpan: any; expanded: boolean; width?: string; }>`
  vertical-align: middle;
  padding: ${props => props.expanded? '0px 0px' : '8px 0px'};
  ${props => props.width? `width: ${props.width}` : null}
`;

const ExpandCell = styled.td<{ expanded: boolean; }>`
  vertical-align: middle;
  padding: ${props => props.expanded? '0px 0px' : '8px 0px'};
  cursor: pointer;
`;

const ExpandedCell = styled.td.attrs(props => ({
  colSpan: props.colSpan
}))<{ colSpan: any; expanded: boolean; }>`
  vertical-align: middle;
  padding: ${props => props.expanded? '0px 0px 8px 0px' : '8px 0px'};
`;

const CellContent = styled.div<{ textAlign?: string; }>`
  height: 50px;
  padding: 15px 10px 10px 10px;
  border-top: 1px solid #ddd;
  border-bottom: 1px solid #ddd;
  text-align: ${props => props.textAlign || 'left'};
  color: #444;
`;

const StartCellContent = styled.div<{ colour: string; textAlign?: string; }>`
  padding: 10px 5px 10px 10px;
  border-top: 1px solid ${props => props.colour || '#DDD'};
  border-bottom: 1px solid ${props => props.colour || '#DDD'};
  border-left: 1px solid ${props => props.colour || '#DDD'};
  border-radius: 5px 0 0 5px;
  background: ${props => props.colour || '#DDD'};
  text-align: ${props => props.textAlign || 'left'};
`;

const PlainStartCellContent = styled.div<{ expanded: boolean; textAlign?: string; }>`
  height: 50px;
  padding: 10px 0px 10px 10px;
  border-radius: ${props => props.expanded? '5px 0 0 0' : '5px 0 0 5px'};
  border-top: 1px solid #ddd;
  border-bottom: 1px solid #ddd;
  border-left: 1px solid #ddd;
  text-align: ${props => props.textAlign || 'left'};
`;

const EndCellContent = styled.div<{ expanded: boolean; }>`
  height: 50px;
  padding: 10px 0px 10px 10px;
  border-top: 1px solid #ddd;
  border-bottom: 1px solid #ddd;
  border-right: 1px solid #ddd;
  border-radius: ${props => props.expanded? '0 5px 0 0' : '0 5px 5px 0'};
  cursor: pointer;
`;

const BlankCellContent = styled.div`
  padding: 10px 10px;
`;

// const ExpandedDetails = styled.div`
//   border-left: 1px solid #ddd;
//   border-bottom: 1px solid #ddd;
//   border-right: 1px solid #ddd;
//   border-radius: 0 0 5px 5px;
//   height: 200px;
// `;

const formatLastData = (position) => {
  if (isUndefinedOrNull(position.status.last_sample)) return 'Not Known';
  return DateTime.fromISO(position.status.last_sample).toFormat('kkkk/LL/dd HH:mm');
}

const formatVoltage = (position) => {
  if (isUndefinedOrNull(position.status.voltage)) return '-';
  return parseFloat(position.status.voltage).toFixed(1) + 'V';
}

const getExceedances = (position) => {
  let exceedances = 0;
  
  if (notUndefined(position.limit_exceedance_summary)) {
    position.limit_exceedance_summary.forEach(limit => {
      if (notUndefined(limit.exceedances)) {
        exceedances += limit.exceedances.length;
      }
    })
  }
  return exceedances;
}

const makeGraphUrl = (position) => {
  if (position.type_id === 2 && position.subtype_id === 0) return `https://${getHost()}/ppv/overview/${position.station_id}`;
  return `https://${getHost()}/positions/graph/${position.station_id}`;
}

export const Position = ({ position, toggleListExpanded }: { position: any; toggleListExpanded: any; }) => {
  const dispatch = useDispatch();
  
  let currentTimebaseColour = position.status.exceedances_level_now || 'green';
  let totalTimebaseColour = position.status.exceedances_level_total|| 'green';
  
  return (
    <React.Fragment>
      <Row>
        {false &&<ExpandCell expanded={position.list_expanded} onClick={() => {dispatch(toggleListExpanded(position.station_id))}}>
          <BlankCellContent>
            <Icon color='#CCC' width='15px' height='15px' icon={position.list_expanded? bxsDownArrow : bxsRightArrow}/>
          </BlankCellContent>
        </ExpandCell>}
        <Cell expanded={position.list_expanded}/>
        {false &&<Cell expanded={position.list_expanded}><StartCellContent colour={connectivity_colour_lookup(position.online_status)}>&nbsp;</StartCellContent></Cell>}
        <Cell expanded={position.list_expanded} width='130px'>
          <PlainStartCellContent expanded={position.list_expanded}>
            <svg xmlns='http://www.w3.org/2000/svg' width='110px' height='30px' viewBox='0 0 110 30'>
              <ConnectivityIcon onlineStatus={position.online_status} />
              <ExceedancesIcon totalTimebaseColour={totalTimebaseColour} currentTimebaseColour={currentTimebaseColour} translate={{x: 35, y: 0}} />
              <BatteryIcon chargeStatus={position.charge_status} translate={{x: 70, y: 0}} />
            </svg>
          </PlainStartCellContent>
        </Cell>
        <Cell expanded={position.list_expanded}><CellContent>{position.title}</CellContent></Cell>
        <Cell expanded={position.list_expanded}><CellContent>{position.site.title}</CellContent></Cell>
        <Cell expanded={position.list_expanded}><CellContent>{complete_type_lookup[position.type_id][position.subtype_id]}</CellContent></Cell>
        <Cell expanded={position.list_expanded}><CellContent textAlign='center'>{getExceedances(position)}</CellContent></Cell>
        <Cell expanded={position.list_expanded}><CellContent>{formatLastData(position)}</CellContent></Cell>
        <Cell expanded={position.list_expanded}><CellContent>{formatVoltage(position)}</CellContent></Cell>
        <Cell expanded={position.list_expanded}>
          <EndCellContent onClick={(e) => { e.preventDefault(); window.parent.location.href=makeGraphUrl(position); }} expanded={position.list_expanded}>
            <Icon height='28px' color='#233567' icon={externalLink} />
          </EndCellContent>
        </Cell>
      
      </Row>
      {position.list_expanded &&
        <Row>
          <ExpandedCell expanded={position.list_expanded}/>
          <ExpandedCell expanded={position.list_expanded} colSpan='8'>
            <PositionDetails position={position}/>
          </ExpandedCell>
        </Row>
      }
    </React.Fragment>
  )
}